/**
 * Exercise player engine - pure logic, no DOM.
 * Constants extracted from main.js Velvet Rhythm player.
 */

// Session states
export const SESSION_STATES = {
  IDLE: 'idle',
  INSTRUCTION: 'instruction',
  BREATHE: 'breathe',
  COUNTDOWN: 'countdown',
  ACTIVE: 'active',
  RELEASE: 'release',
  REST: 'rest',
  DONE: 'done',
};

// Countdown runs for 3 seconds (one tick per number: 3 → 2 → 1 → SQUEEZE).
export const COUNTDOWN_MS = 3000;

// Ring constants (from main.js lines 3188-3210)
export const RING_SIZES = { r1: 248, r2: 170, r3: 92 };
export const DOT_SIZE = 16;

export const RING_SCALES = {
  neutral: { r1: 1.00, r2: 1.00, r3: 1.00 },
  squeeze: { r1: 0.65, r2: 0.55, r3: 0.45 },
  relax:   { r1: 1.00, r2: 1.00, r3: 1.00 },
  push:    { r1: 1.15, r2: 1.25, r3: 1.35 },
};

export const RING_COLORS = {
  squeeze: { active: '#C2185B', dim: '#321425' },
  relax:   { active: '#5C9EAD', dim: '#321425' },
  push:    { active: '#E07850', dim: '#321425' },
  neutral: { active: '#321425', dim: '#321425' },
};

export const RING_OPACITIES = {
  neutral: { r1: 0.35, r2: 0.35, r3: 0.35 },
  squeeze: { r1: 0.9,  r2: 1.0,  r3: 1.0 },
  relax:   { r1: 0.8,  r2: 0.9,  r3: 1.0 },
  push:    { r1: 0.75, r2: 0.88, r3: 1.0 },
};

export const EASINGS = {
  squeezeIn:  'cubic-bezier(0.22, 0.61, 0.36, 1)',
  squeezeOut: 'cubic-bezier(0.4, 0, 0.6, 1)',
  relaxOut:   'cubic-bezier(0.0, 0.0, 0.2, 1)',
  pushIn:     'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
};

// Stagger calculation (from main.js lines 3248-3262)
export function calculateStagger(holdSeconds, mode) {
  if (mode === 'squeeze') {
    const budget = Math.max(200, holdSeconds * 0.30 * 1000);
    return {
      delays: [0, budget * 0.5, budget],
      duration: Math.min(Math.max(budget * 0.5, 400), 900),
    };
  }
  if (mode === 'relax') {
    return { delays: [800, 400, 0], duration: 600 };
  }
  if (mode === 'push') {
    return { delays: [0, 150, 300], duration: 700 };
  }
  // pulse or default
  return { delays: [0, 0, 0], duration: 150 };
}

// State transition logic
export function getNextState(currentState, exercise, currentSet) {
  switch (currentState) {
    case SESSION_STATES.IDLE:        return SESSION_STATES.INSTRUCTION;
    case SESSION_STATES.INSTRUCTION: return SESSION_STATES.COUNTDOWN;
    case SESSION_STATES.BREATHE:     return SESSION_STATES.COUNTDOWN;
    case SESSION_STATES.COUNTDOWN:   return SESSION_STATES.ACTIVE;
    case SESSION_STATES.ACTIVE:      return SESSION_STATES.RELEASE;
    case SESSION_STATES.RELEASE:
      if (currentSet >= exercise.sets) return SESSION_STATES.DONE;
      if ((exercise.rest ?? 0) > 0)   return SESSION_STATES.REST;
      // Between-set countdown is gone: once the user is mid-session
      // the 3-2-1 reads as a TV ad break, not a fresh start. The first
      // countdown after Begin still orients her; subsequent sets roll
      // straight into ACTIVE.
      return SESSION_STATES.ACTIVE;
    case SESSION_STATES.REST:        return SESSION_STATES.ACTIVE;
    default: return SESSION_STATES.DONE;
  }
}

// Duration for each state in milliseconds
export function getStateDuration(state, exercise) {
  switch (state) {
    case SESSION_STATES.BREATHE:   return 2500;
    case SESSION_STATES.COUNTDOWN: return COUNTDOWN_MS;
    case SESSION_STATES.ACTIVE:    return (exercise.hold ?? 5) * 1000;
    case SESSION_STATES.RELEASE:   return (exercise.relax ?? exercise.hold ?? 2) * 1000;
    case SESSION_STATES.REST:      return (exercise.rest ?? 0) * 1000;
    default: return 0;
  }
}

// ─── Context-aware API ──────────────────────────────────────────────────
//
// The engine below models all three exercise types (hold/hold-push,
// pulse, alt) through a single state machine. Consumers hold an opaque
// `ctx` object and call `advance(state, exercise, ctx)` to get the next
// `{state, ctx}` pair. Durations and ring modes are also ctx-aware so
// alt and pulse can vary per-phase without special-casing in the UI.
//
// ctx shape:
//   setIdx    — 0-indexed current set (0..sets-1)
//   repIdx    — 0-indexed rep within a pulse set (pulse only)
//   altPhase  — 0 (phase A / squeeze) | 1 (phase B / relax|push), alt only
//
// The older `getNextState` / `getStateDuration` exports are kept intact
// so any existing consumer continues to work. New code should prefer
// the ctx-aware API.

// Build an initial ctx. Always start at set 0, rep 0, alt phase 0.
export function initCtx() {
  return { setIdx: 0, repIdx: 0, altPhase: 0 };
}

// Exercise type normalization. 'hold-push' is treated the same as 'hold'
// here — the visual difference lives in movementMode / RING_COLORS,
// not in the state machine.
function normType(exercise) {
  const t = exercise?.type;
  if (t === 'pulse' || t === 'alt') return t;
  return 'hold';
}

// Phase B of an alt exercise can be either a relax (default) or a
// gentle push. Mirrors main.js: ex.push flag OR altB string containing
// "LENGTHEN" marks phase B as push.
function altBIsPush(exercise) {
  if (exercise?.push) return true;
  const b = exercise?.altB;
  return typeof b === 'string' && b.toUpperCase().includes('LENGTHEN');
}

// Advance the state machine. Returns a fresh `{state, ctx}` — never
// mutates the ctx passed in.
export function advance(state, exercise, ctx) {
  const c = ctx || initCtx();
  const type = normType(exercise);
  const sets = exercise?.sets ?? 1;
  const reps = exercise?.reps ?? 1;
  const hasRest = (exercise?.rest ?? 0) > 0;

  switch (state) {
    case SESSION_STATES.IDLE:
      return { state: SESSION_STATES.INSTRUCTION, ctx: c };
    case SESSION_STATES.INSTRUCTION:
      // Was BREATHE; now runs a 3-2-1 countdown before the first set.
      return { state: SESSION_STATES.COUNTDOWN, ctx: c };
    case SESSION_STATES.BREATHE:
      // Kept for backwards compat with any external caller; treated as
      // a passthrough to the new COUNTDOWN phase.
      return { state: SESSION_STATES.COUNTDOWN, ctx: c };
    case SESSION_STATES.COUNTDOWN:
      return { state: SESSION_STATES.ACTIVE, ctx: c };

    case SESSION_STATES.ACTIVE: {
      if (type === 'alt') {
        // Alt has no RELEASE phase — each set is a standalone ACTIVE.
        // Flip phase and advance the set counter at the same time.
        const nextSetIdx = c.setIdx + 1;
        if (nextSetIdx >= sets) return { state: SESSION_STATES.DONE, ctx: c };
        const nextAltPhase = 1 - c.altPhase;
        const nextCtx = { ...c, setIdx: nextSetIdx, altPhase: nextAltPhase };
        // Rest only after a full A→B pair, i.e. when we're about to
        // start phase A again (altPhase becomes 0) and rest > 0.
        if (nextAltPhase === 0 && hasRest) {
          return { state: SESSION_STATES.REST, ctx: nextCtx };
        }
        return { state: SESSION_STATES.ACTIVE, ctx: nextCtx };
      }
      // hold / pulse: ACTIVE → RELEASE
      return { state: SESSION_STATES.RELEASE, ctx: c };
    }

    case SESSION_STATES.RELEASE: {
      // Pulse, and holds that opt in with repeatHolds (e.g. the public
      // Class 1 homework: 7 squeezes per set, then rest), count `reps`
      // within a set before moving on. Other holds keep one hold per set.
      if (type === 'pulse' || (type === 'hold' && exercise?.repeatHolds)) {
        const nextRepIdx = c.repIdx + 1;
        if (nextRepIdx < reps) {
          // More pulses remain in this set — back to ACTIVE directly,
          // no countdown between reps. Matches main.js pulseRelax→runPulse.
          return { state: SESSION_STATES.ACTIVE, ctx: { ...c, repIdx: nextRepIdx } };
        }
        // Set finished.
        const nextSetIdx = c.setIdx + 1;
        if (nextSetIdx >= sets) return { state: SESSION_STATES.DONE, ctx: c };
        const nextCtx = { ...c, repIdx: 0, setIdx: nextSetIdx };
        // Pulse between-set: rest if configured, else straight back
        // to ACTIVE (main.js runRest or runPulse — no countdown).
        if (hasRest) return { state: SESSION_STATES.REST, ctx: nextCtx };
        return { state: SESSION_STATES.ACTIVE, ctx: nextCtx };
      }
      // hold / hold-push: RELEASE → next set or DONE. The between-set
      // countdown is gone for every type — once she's in the rhythm
      // the 3-2-1 reset reads as an interruption.
      const nextSetIdx = c.setIdx + 1;
      if (nextSetIdx >= sets) return { state: SESSION_STATES.DONE, ctx: c };
      const nextCtx = { ...c, setIdx: nextSetIdx };
      if (hasRest) return { state: SESSION_STATES.REST, ctx: nextCtx };
      return { state: SESSION_STATES.ACTIVE, ctx: nextCtx };
    }

    case SESSION_STATES.REST:
      // All types roll straight back into ACTIVE. The countdown only
      // ever fires once per session — at the start, before set 1 —
      // so subsequent set transitions feel like one continuous
      // practice rather than a series of fresh starts.
      return { state: SESSION_STATES.ACTIVE, ctx: c };

    default:
      return { state: SESSION_STATES.DONE, ctx: c };
  }
}

// ctx-aware duration. Same fallback defaults as getStateDuration.
export function getDurationMs(state, exercise, ctx) {
  const c = ctx || initCtx();
  const type = normType(exercise);
  switch (state) {
    case SESSION_STATES.BREATHE:
      return 2500;
    case SESSION_STATES.COUNTDOWN:
      return COUNTDOWN_MS;
    case SESSION_STATES.ACTIVE:
      if (type === 'pulse') return (exercise?.on ?? 1) * 1000;
      if (type === 'alt') {
        // Phase A = squeeze hold, phase B = relax (or push, same duration).
        return c.altPhase === 0
          ? (exercise?.hold ?? 2) * 1000
          : (exercise?.relax ?? exercise?.hold ?? 2) * 1000;
      }
      return (exercise?.hold ?? 5) * 1000;
    case SESSION_STATES.RELEASE:
      if (type === 'pulse') return (exercise?.off ?? 1) * 1000;
      return (exercise?.relax ?? exercise?.hold ?? 2) * 1000;
    case SESSION_STATES.REST:
      return (exercise?.rest ?? 0) * 1000;
    default:
      return 0;
  }
}

// Ring animation mode for the current state. Used to pick
// RING_SCALES / RING_COLORS / RING_OPACITIES / EASINGS.
export function getRingMode(state, exercise, ctx) {
  const c = ctx || initCtx();
  const type = normType(exercise);
  if (state === SESSION_STATES.COUNTDOWN) return 'neutral';
  if (state === SESSION_STATES.ACTIVE) {
    if (type === 'pulse') return exercise?.push ? 'push' : 'squeeze';
    if (type === 'alt') {
      if (c.altPhase === 0) return 'squeeze';
      return altBIsPush(exercise) ? 'push' : 'relax';
    }
    return exercise?.movementMode || 'squeeze';
  }
  if (state === SESSION_STATES.RELEASE) return 'relax';
  return 'neutral';
}
